import { Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Template = { id: string; code: string; name: string; sequence_no: number; description?: string; student_stage_count?: number };
type Stage = {
  id: string; status: string; template_code: string; template_name: string; evidence_count?: number;
  admission_no?: string; first_name?: string; last_name?: string;
};

export default function HpcHubPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const status = params.get('status') || undefined;
  const permissions = useAuthStore((s) => s.user?.permissions || []);
  const canManage = permissions.includes('education.hpc.manage') || permissions.includes('education.admin');
  const templates = useQuery({
    queryKey: ['education-hpc-templates'],
    queryFn: async () => (await api.get<Template[]>('/education/hpc/templates')).data || [],
  });
  const stages = useQuery({
    queryKey: ['education-hpc-stages', q, status],
    queryFn: async () => (await api.get<Stage[]>('/education/hpc/stages', { params: { q: q || undefined, status } })).data || [],
  });
  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title="Holistic Progress Card"
      description="Company-defined progress stages with text and URL evidence links. No grades or accreditation scores are generated."
      extra={canManage ? <Space>
        <Button onClick={() => navigate('/education/hpc/templates/new')}>New stage template</Button>
        <Button type="primary" onClick={() => navigate('/education/hpc/stages/new')}>Assign student stage</Button>
      </Space> : undefined}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title="Stage templates">
          <Table
            rowKey="id"
            size="small"
            loading={templates.isLoading}
            dataSource={templates.data}
            pagination={false}
            columns={[
              { title: 'Sequence', dataIndex: 'sequence_no', width: 90 },
              { title: 'Code', dataIndex: 'code', width: 140 },
              { title: 'Name', dataIndex: 'name' },
              { title: 'Assigned stages', dataIndex: 'student_stage_count', width: 130 },
              {
                title: 'Actions', width: 90,
                render: (_: unknown, row: Template) => canManage
                  ? <Button size="small" onClick={() => navigate(`/education/hpc/templates/${row.id}`)}>Edit</Button>
                  : null,
              },
            ]}
          />
        </Card>
        <Card size="small" title="Student stages">
          <Space wrap style={{ marginBottom: 16 }}>
            <Input.Search
              allowClear defaultValue={q} style={{ width: 300 }}
              placeholder="Student, admission number, or stage"
              onSearch={(value) => setFilter('q', value.trim())}
            />
            <Select
              allowClear value={status} placeholder="Status" style={{ width: 160 }}
              onChange={(value) => setFilter('status', value)}
              options={['open', 'in_progress', 'completed'].map((value) => ({ value, label: value.replace('_', ' ') }))}
            />
          </Space>
          <Table
            rowKey="id"
            size="small"
            loading={stages.isLoading}
            dataSource={stages.data}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: 'Student',
                render: (_: unknown, row: Stage) => (
                  <Space direction="vertical" size={0}>
                    <Typography.Text>{[row.first_name, row.last_name].filter(Boolean).join(' ')}</Typography.Text>
                    <Typography.Text type="secondary">{row.admission_no}</Typography.Text>
                  </Space>
                ),
              },
              { title: 'Stage', render: (_: unknown, row: Stage) => `${row.template_code} — ${row.template_name}` },
              { title: 'Status', dataIndex: 'status', width: 130, render: (value: string) => <Tag>{value.replace('_', ' ')}</Tag> },
              { title: 'Evidence', dataIndex: 'evidence_count', width: 90 },
              { title: 'Actions', width: 90, render: (_: unknown, row: Stage) => <Button size="small" onClick={() => navigate(`/education/hpc/stages/${row.id}`)}>Open</Button> },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
